import * as Sentry from "@sentry/react";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, CheckCircle2, Wrench, ShieldAlert, Bug } from "lucide-react";

export default function AdminMaintenancePage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const [backendResult, setBackendResult] = useState<{ eventId: string; release: string } | null>(null);
  const [frontendEventId, setFrontendEventId] = useState<string | null>(null);

  const { data, isLoading } = useQuery<{ enabled: boolean }>({
    queryKey: ["/api/maintenance"],
  });

  const toggleMutation = useMutation({
    mutationFn: (enabled: boolean) =>
      apiRequest("POST", "/api/admin/maintenance", { enabled }),
    onSuccess: (_, enabled) => {
      queryClient.invalidateQueries({ queryKey: ["/api/maintenance"] });
      toast({
        title: enabled ? "Maintenance mode enabled" : "Maintenance mode disabled",
        description: enabled
          ? "Users will now see the maintenance page."
          : "The site is back online for all users.",
      });
    },
    onError: () => {
      toast({ title: "Failed to update maintenance mode", variant: "destructive" });
    },
  });

  const sentryBackendMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/sentry-test");
      return res.json() as Promise<{ ok: boolean; eventId: string; release: string }>;
    },
    onSuccess: (data) => {
      setBackendResult(data);
      toast({ title: "Backend test error sent to Sentry", description: `Event ID: ${data.eventId}` });
    },
    onError: () => {
      toast({ title: "Failed to send backend Sentry test", variant: "destructive" });
    },
  });

  function triggerFrontendSentryTest() {
    const testError = new Error("[Sentry source-map test] Deliberate frontend test error");
    const eventId = Sentry.captureException(testError);
    setFrontendEventId(eventId);
    toast({ title: "Frontend test error sent to Sentry", description: `Event ID: ${eventId}` });
  }

  const enabled = data?.enabled ?? false;

  return (
    <div className="p-6 max-w-2xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="text-maintenance-title">
          <Wrench className="h-6 w-6" />
          Maintenance Mode
        </h1>
        <p className="text-muted-foreground mt-1">
          Temporarily take the site offline for all users while you deploy updates.
        </p>
      </div>

      <Card data-testid="card-maintenance-status">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Current Status</CardTitle>
            {isLoading ? (
              <Badge variant="outline">Loading...</Badge>
            ) : enabled ? (
              <Badge className="bg-orange-500 hover:bg-orange-500 text-white gap-1" data-testid="badge-maintenance-on">
                <AlertTriangle className="h-3 w-3" />
                Maintenance On
              </Badge>
            ) : (
              <Badge className="bg-green-600 hover:bg-green-600 text-white gap-1" data-testid="badge-maintenance-off">
                <CheckCircle2 className="h-3 w-3" />
                Site Online
              </Badge>
            )}
          </div>
          <CardDescription>
            {enabled
              ? "The site is currently under maintenance. All non-admin users see the maintenance page."
              : "The site is live and accessible to all users."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {enabled ? (
            <div className="rounded-lg border border-orange-200 bg-orange-50 dark:bg-orange-950/20 dark:border-orange-900 p-4 flex gap-3">
              <AlertTriangle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-orange-800 dark:text-orange-300">Maintenance mode is active</p>
                <p className="text-orange-700 dark:text-orange-400 mt-0.5">
                  Users visiting the site will see a maintenance message. You can still access all admin pages as normal.
                </p>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border border-green-200 bg-green-50 dark:bg-green-950/20 dark:border-green-900 p-4 flex gap-3">
              <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-green-800 dark:text-green-300">Site is online</p>
                <p className="text-green-700 dark:text-green-400 mt-0.5">
                  All users have full access. Enable maintenance mode before deploying updates.
                </p>
              </div>
            </div>
          )}

          <div className="flex gap-3">
            {enabled ? (
              <Button
                onClick={() => toggleMutation.mutate(false)}
                disabled={toggleMutation.isPending}
                className="bg-green-600 hover:bg-green-700 text-white"
                data-testid="button-disable-maintenance"
              >
                <CheckCircle2 className="h-4 w-4 mr-2" />
                {toggleMutation.isPending ? "Updating..." : "Take Site Back Online"}
              </Button>
            ) : (
              <Button
                onClick={() => toggleMutation.mutate(true)}
                disabled={toggleMutation.isPending}
                variant="destructive"
                data-testid="button-enable-maintenance"
              >
                <AlertTriangle className="h-4 w-4 mr-2" />
                {toggleMutation.isPending ? "Updating..." : "Enable Maintenance Mode"}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Bug className="h-4 w-4" />
            Sentry Source Map Verification
          </CardTitle>
          <CardDescription>
            After a production deploy, send deliberate test errors to confirm Sentry resolves stack
            traces to readable filenames and line numbers rather than minified code.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => sentryBackendMutation.mutate()}
              disabled={sentryBackendMutation.isPending}
              data-testid="button-sentry-test-backend"
            >
              <Bug className="h-4 w-4 mr-2" />
              {sentryBackendMutation.isPending ? "Sending..." : "Test backend error"}
            </Button>
            <Button
              variant="outline"
              onClick={triggerFrontendSentryTest}
              data-testid="button-sentry-test-frontend"
            >
              <Bug className="h-4 w-4 mr-2" />
              Test frontend error
            </Button>
          </div>

          {(backendResult || frontendEventId) && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 dark:bg-blue-950/20 dark:border-blue-900 p-4 space-y-2 text-sm">
              <p className="font-medium text-blue-800 dark:text-blue-300">Test events sent</p>
              {backendResult && (
                <div className="space-y-0.5 text-blue-700 dark:text-blue-400">
                  <p><span className="font-mono font-semibold">Backend event ID:</span> {backendResult.eventId}</p>
                  <p><span className="font-mono font-semibold">Backend release:</span> {backendResult.release}</p>
                </div>
              )}
              {frontendEventId && (
                <p className="text-blue-700 dark:text-blue-400">
                  <span className="font-mono font-semibold">Frontend event ID:</span> {frontendEventId}
                </p>
              )}
              <p className="text-blue-600 dark:text-blue-500 mt-1">
                Find these in Sentry and confirm the stack trace shows readable filenames and line
                numbers. The release tag should match the git SHA from the current deploy.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" />
            How it works
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground space-y-2">
          <p>When maintenance mode is <strong>on</strong>:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>All regular users see a maintenance message page</li>
            <li>Admin accounts (like yours) continue to have full access</li>
            <li>No data is affected — everything resumes instantly when you turn it off</li>
          </ul>
          <p className="mt-2">Recommended workflow:</p>
          <ol className="list-decimal list-inside space-y-1 ml-2">
            <li>Enable maintenance mode here</li>
            <li>Click <strong>Publish</strong> to deploy your updates</li>
            <li>Once the new version is live, disable maintenance mode</li>
          </ol>
        </CardContent>
      </Card>
    </div>
  );
}
